/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { FC, memo } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import type * as Type from '@/common/interface';
import { Avatar } from '@/components';
import { floppyNavigation, isDarkTheme } from '@/utils';
import { userCenterStore } from '@/stores';
import { REACT_BASE_PATH } from '@/router/alias';

interface Props {
  redDot: Type.NotificationStatus | undefined;
  userInfo: Type.UserInfoRes;
  logOut: (e) => void;
}

const Index: FC<Props> = ({ redDot, userInfo, logOut }) => {
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { agent: ucAgent } = userCenterStore();
  const displayName = userInfo?.display_name || userInfo?.username || '已登录';
  const handleLinkClick = (evt) => {
    if (floppyNavigation.shouldProcessLinkClick(evt)) {
      evt.preventDefault();
      const href = evt.currentTarget.getAttribute('href');
      floppyNavigation.navigate(href, {
        handler: navigate,
      });
    }
  };
  return (
    <Dropdown
      align="end"
      className="aito-account-menu"
      data-bs-theme={isDarkTheme() ? 'dark' : 'light'}>
      <Dropdown.Toggle
        variant="success"
        id="dropdown-basic"
        as="button"
        className="aito-account-toggle no-toggle pointer">
        <span className="aito-account-avatar">
          <Avatar
            size="28px"
            avatar={userInfo?.avatar}
            alt={userInfo?.display_name}
            searchStr="s=96"
          />
        </span>
        <span className="aito-account-copy">
          <strong>{displayName}</strong>
          <small>已登录</small>
        </span>
      </Dropdown.Toggle>

      <Dropdown.Menu className="position-absolute">
        <Dropdown.Header>{displayName}</Dropdown.Header>
        <Dropdown.Item href={ucAgent?.agent_info?.url || '/console'}>
          控制台
        </Dropdown.Item>
        <Dropdown.Item href="/console/token">获取密钥</Dropdown.Item>
        <Dropdown.Item href="/console/topup">获取额度</Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item
          href={`${REACT_BASE_PATH}/users/${userInfo.username}`}
          onClick={handleLinkClick}>
          个人主页
        </Dropdown.Item>
        <Dropdown.Item
          href={`${REACT_BASE_PATH}/users/settings/profile`}
          onClick={handleLinkClick}>
          账号设置
        </Dropdown.Item>
        {userInfo?.role_id === 2 ? (
          <Dropdown.Item
            href={`${REACT_BASE_PATH}/admin`}
            onClick={handleLinkClick}>
            管理后台
          </Dropdown.Item>
        ) : null}
        <Dropdown.Divider />
        <Dropdown.Item
          href={`${REACT_BASE_PATH}/users/logout`}
          onClick={(e) => logOut(e)}>
          退出登录
        </Dropdown.Item>
      </Dropdown.Menu>
      {(redDot?.inbox || 0) > 0 ? <span className="aito-account-dot" /> : null}
    </Dropdown>
  );
};

export default memo(Index);
